import React from "react"
import { Button } from "react-bootstrap"

import FormikBootstrap from "@/components/formik-bootstrap"
import * as TieredRatesheet from "@/pricing/models/TieredRatesheet"

import AddRateCategory from "./AddRateCategory"
import TieredRatesheetTable from "./tiered_ratesheet_editor/TieredRatesheetTable"

const TieredRatesheetEditor = ({
  name,
  value,
  onChange,
  readOnly,
  canEditPricingStructure,
}): JSX.Element => {
  const rateCategories = TieredRatesheet.rateCategories(value)

  const newChangeEvent = (value) => ({ target: { name, value } })

  const triggerChange = (value) => onChange(newChangeEvent(value))

  const appendTier = (tier) => {
    triggerChange(TieredRatesheet.addTier(value, tier))
  }

  const addTier = () => {
    if (value.tiers.length === 0) {
      appendTier(TieredRatesheet.newTier({ rateKeys: rateCategories }))
    } else {
      appendTier(
        TieredRatesheet.newTier({
          tierFrom: TieredRatesheet.nextTierFrom(value),
          rateKeys: rateCategories,
        })
      )
    }
  }

  const removeTier = (idx) => {
    triggerChange(TieredRatesheet.removeTierAt(value, idx))
  }

  const removeCategory = (category) => {
    triggerChange(TieredRatesheet.removeCategory(value, category))
  }

  const shiftCategoryLeft = (category) => {
    triggerChange(TieredRatesheet.moveCategoryLeft(value, category))
  }

  const shiftCategoryRight = (category) => {
    triggerChange(TieredRatesheet.moveCategoryRight(value, category))
  }

  const addCategory = (category) => {
    let newSheet = TieredRatesheet.addCategory(value, category)

    if (newSheet.tiers.length === 0) {
      newSheet = TieredRatesheet.addTier(
        newSheet,
        TieredRatesheet.newTier({ rateKeys: TieredRatesheet.rateCategories(newSheet) })
      )
    }

    triggerChange(newSheet)
  }

  return (
    <div className="d-flex flex-column gap-2 mt-1">
      <FormikBootstrap.Checkbox
        name={`${name}.perTier`}
        label="Price per tier?"
        readOnly={readOnly}
        disabled={!canEditPricingStructure}
        helpText={
          <span className="text-wrap">
            Enabled: Price is calculated by packing passengers across all tiers.
            <br />
            Disabled: Use the first single tier that holds all passengers.
          </span>
        }
      />
      <FormikBootstrap.Checkbox
        name={`${name}.independentCategories`}
        label="Independent Rate Categories?"
        readOnly={readOnly}
        disabled={!canEditPricingStructure}
        helpText={
          <span className="text-wrap">
            Enabled: Each rate category functions as an independent tier system.
            <br />
            Disabled: the tier selection occurs on the conjoined passenger counts.
          </span>
        }
      />
      <TieredRatesheetTable
        name={name}
        readOnly={readOnly}
        ratesheet={value}
        canEditPricingStructure={canEditPricingStructure}
        onRemoveTier={removeTier}
        onRemoveCategory={removeCategory}
        onShiftCategoryLeft={shiftCategoryLeft}
        onShiftCategoryRight={shiftCategoryRight}
      />
      {!readOnly && canEditPricingStructure && (
        <div>
          <Button size="sm" onClick={() => addTier()}>
            <i className="fas fa-plus me-2" />
            Tier
          </Button>
        </div>
      )}
      {!readOnly && canEditPricingStructure && (
        <AddRateCategory
          rateCategories={TieredRatesheet.rateCategories(value)}
          onClick={(ctg) => addCategory(ctg)}
        />
      )}
    </div>
  )
}

export default TieredRatesheetEditor
